import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import QuizLayout from "./components/quiz/QuizLayout";
import Step1 from "./components/quiz/steps/Step1";
import Step2 from "./components/quiz/steps/Step2";
import Step3 from "./components/quiz/steps/Step3";
import Step4 from "./components/quiz/steps/Step4";
import Step5 from "./components/quiz/steps/Step5";
import Step6 from "./components/quiz/steps/Step6";
import Step7 from "./components/quiz/steps/Step7";
import Step8 from "./components/quiz/steps/Step8";
import Step9 from "./components/quiz/steps/Step9";
import Step10 from "./components/quiz/steps/Step10";
import Step11 from "./components/quiz/steps/Step11";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import SplitTests from "./pages/SplitTests";
import SplitTestCreate from "./pages/SplitTestCreate";
import SplitTestResults from "./pages/SplitTestResults";

const TOTAL_STEPS = 11;
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Step names for analytics
const STEP_NAMES = {
  1: 'Poop Frequency',
  2: 'Symptoms',
  3: 'Meet Hashi-aid',
  4: 'Support Areas',
  5: 'Whole Body Health',
  6: 'Current Supplements',
  7: 'Absorption Info',
  8: 'Comparison',
  9: 'Clinical Research',
  10: 'Timeline',
  11: 'Email Capture'
};

// Generate session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('quiz_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('quiz_session_id', sessionId);
  }
  return sessionId;
};

// GA4 tracking helper
const trackGA4Event = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

// Meta Pixel tracking helper
const trackMetaEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
  }
};

// Track custom Meta events
const trackMetaCustomEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', eventName, parameters);
  }
};

// Backend tracking helper
const trackBackendEvent = async (eventData) => {
  try {
    await fetch(`${API_URL}/api/quiz/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData)
    });
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

// Create session on backend
const createBackendSession = async (sessionId) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    await fetch(`${API_URL}/api/quiz/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        user_agent: navigator.userAgent,
        referrer: document.referrer,
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign')
      })
    });
  } catch (error) {
    console.error('Failed to create session:', error);
  }
};

const Quiz = ({ testSlug = "" }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [sessionId] = useState(getSessionId);
  const [activeTest, setActiveTest] = useState(null);
  const [variant, setVariant] = useState(null);
  const [lpLink, setLpLink] = useState("");
  const [quizData, setQuizData] = useState({
    poopFrequency: null,
    symptoms: [],
    supportAreas: [],
    takesSupplement: null,
    clinicalImportance: null,
  });

  // Fetch split test config and default LP link
  useEffect(() => {
    const fetchTestConfig = async () => {
      try {
        // Fetch LP link settings
        const settingsRes = await fetch(`${API_URL}/api/settings`);
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          setLpLink(settings.lp_link || "");
        }

        // Fetch active test
        let testData = null;
        if (testSlug) {
          const res = await fetch(`${API_URL}/api/split-tests/public/by-slug/${testSlug}`);
          if (res.ok) {
            const data = await res.json();
            testData = data.test;
          }
        } else {
          const res = await fetch(`${API_URL}/api/split-tests/public/default`);
          if (res.ok) {
            const data = await res.json();
            testData = data.tests?.[0] || null;
          }
        }

        if (testData) {
          setActiveTest(testData);
          if (testData.lp_link) setLpLink(testData.lp_link);

          // Assign variant (sticky per session)
          const storageKey = `test_variant_${testData.id}`;
          const stored = sessionStorage.getItem(storageKey);
          if (stored) {
            setVariant(stored);
          } else {
            const rand = Math.random() * 100;
            const assigned = rand < testData.ratio[0] ? "control" : "variant";
            sessionStorage.setItem(storageKey, assigned);
            setVariant(assigned);
          }
        }
      } catch (err) {
        console.error('Failed to fetch test config:', err);
      }
    };
    fetchTestConfig();
  }, [testSlug]);

  // Track initial quiz start
  useEffect(() => {
    // Create backend session with test info
    const sessionData = {
      session_id: sessionId,
      user_agent: navigator.userAgent,
      referrer: document.referrer,
      utm_source: new URLSearchParams(window.location.search).get('utm_source'),
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
    };
    if (activeTest) {
      sessionData.test_id = activeTest.id;
      sessionData.variant = variant;
      sessionData.quiz_slug = testSlug;
    }
    fetch(`${API_URL}/api/quiz/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sessionData)
    }).catch(() => {});
    
    // GA4
    trackGA4Event('quiz_started', {
      quiz_name: 'Hashi-aid Quiz',
      total_steps: TOTAL_STEPS,
      ...(activeTest ? { test_id: activeTest.id, variant } : {})
    });
    // Meta Pixel
    trackMetaCustomEvent('QuizStarted', {
      quiz_name: 'Hashi-aid Quiz',
      total_steps: TOTAL_STEPS
    });
    // Backend
    trackBackendEvent({
      session_id: sessionId,
      event_type: 'quiz_started',
      metadata: {
        quiz_name: 'Hashi-aid Quiz',
        total_steps: TOTAL_STEPS,
        ...(activeTest ? { test_id: activeTest.id, variant } : {})
      }
    });
  }, [sessionId, activeTest, variant, testSlug]);

  // Helper to get test metadata for tracking
  const getTestMeta = () => activeTest ? { test_id: activeTest.id, variant } : {};

  // Get step overrides for current step (only for variant group)
  const getStepOverrides = (stepNum) => {
    if (!activeTest || variant !== "variant") return null;
    const override = activeTest.step_overrides?.[String(stepNum)];
    if (!override) return null;
    // Only return override if it has actual content
    if (override.question || override.subtitle || (override.options && override.options.length > 0)) {
      return override;
    }
    return null;
  };

  // Track step views
  useEffect(() => {
    const progressPercent = Math.round((currentStep / TOTAL_STEPS) * 100);
    
    // GA4
    trackGA4Event('quiz_step_viewed', {
      step_number: currentStep,
      step_name: STEP_NAMES[currentStep],
      total_steps: TOTAL_STEPS,
      progress_percent: progressPercent,
      ...getTestMeta()
    });

    // Meta Pixel - ViewContent for each step
    trackMetaEvent('ViewContent', {
      content_name: STEP_NAMES[currentStep],
      content_category: 'Quiz Step',
      content_ids: [`step_${currentStep}`],
      value: currentStep,
      currency: 'USD'
    });

    // Backend
    trackBackendEvent({
      session_id: sessionId,
      event_type: 'quiz_step_viewed',
      step_number: currentStep,
      step_name: STEP_NAMES[currentStep],
      metadata: { progress_percent: progressPercent, ...getTestMeta() }
    });

    // Track quiz completion when reaching final step
    if (currentStep === TOTAL_STEPS) {
      // GA4
      trackGA4Event('quiz_completed', {
        quiz_name: 'Hashi-aid Quiz',
        total_steps: TOTAL_STEPS
      });
      // Meta Pixel - Lead event for quiz completion
      trackMetaEvent('Lead', {
        content_name: 'Hashi-aid Quiz Completed',
        content_category: 'Quiz',
        value: 0,
        currency: 'USD'
      });
      // Backend
      trackBackendEvent({
        session_id: sessionId,
        event_type: 'quiz_completed',
        step_number: currentStep,
        step_name: STEP_NAMES[currentStep],
        metadata: { ...getTestMeta() }
      });
    }
  }, [currentStep, sessionId, activeTest, variant]);

  const handleSelect = (field, value) => {
    setQuizData(prev => ({
      ...prev,
      [field]: value
    }));

    // GA4
    trackGA4Event('quiz_answer_selected', {
      step_number: currentStep,
      step_name: STEP_NAMES[currentStep],
      field: field,
      value: Array.isArray(value) ? value.join(', ') : value
    });

    // Meta Pixel
    trackMetaCustomEvent('QuizAnswerSelected', {
      step_number: currentStep,
      step_name: STEP_NAMES[currentStep],
      field: field
    });

    // Backend
    trackBackendEvent({
      session_id: sessionId,
      event_type: 'quiz_answer_selected',
      step_number: currentStep,
      step_name: STEP_NAMES[currentStep],
      field: field,
      value: Array.isArray(value) ? value.join(', ') : value,
      metadata: { ...getTestMeta() }
    });
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      // GA4
      trackGA4Event('quiz_step_continued', {
        from_step: currentStep,
        from_step_name: STEP_NAMES[currentStep],
        to_step: currentStep + 1,
        to_step_name: STEP_NAMES[currentStep + 1]
      });

      // Meta Pixel
      trackMetaCustomEvent('QuizStepContinued', {
        from_step: currentStep,
        to_step: currentStep + 1,
        progress_percent: Math.round(((currentStep + 1) / TOTAL_STEPS) * 100)
      });

      // Backend
      trackBackendEvent({
        session_id: sessionId,
        event_type: 'quiz_step_continued',
        step_number: currentStep,
        step_name: STEP_NAMES[currentStep],
        metadata: { to_step: currentStep + 1 }
      });
      
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // GA4
      trackGA4Event('quiz_step_back', {
        from_step: currentStep,
        from_step_name: STEP_NAMES[currentStep],
        to_step: currentStep - 1,
        to_step_name: STEP_NAMES[currentStep - 1]
      });

      // Backend
      trackBackendEvent({
        session_id: sessionId,
        event_type: 'quiz_step_back',
        step_number: currentStep,
        step_name: STEP_NAMES[currentStep],
        metadata: { to_step: currentStep - 1 }
      });
      
      setCurrentStep(prev => prev - 1);
    }
  };

  const renderStep = () => {
    const ov = getStepOverrides(currentStep);
    switch (currentStep) {
      case 1:
        return <Step1 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 2:
        return <Step2 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 3:
        return <Step3 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 4:
        return <Step4 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 5:
        return <Step5 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 6:
        return <Step6 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 7:
        return <Step7 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 8:
        return <Step8 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 9:
        return <Step9 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 10:
        return <Step10 data={quizData} onSelect={handleSelect} overrides={ov} />;
      case 11:
        return <Step11 data={quizData} onSelect={handleSelect} overrides={ov} lpLink={lpLink} />;
      default:
        return null;
    }
  };

  return (
    <QuizLayout
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS}
      onBack={handleBack}
      onContinue={handleNext}
      showBack={currentStep > 1}
      showContinue={currentStep < TOTAL_STEPS}
      showNavigation={currentStep < TOTAL_STEPS}
    >
      {renderStep()}
    </QuizLayout>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Quiz wrapper for custom slug URLs
const QuizWithSlug = () => {
  const { slug } = useParams();
  return <Quiz testSlug={slug} />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="/admin/split-tests" element={<ProtectedRoute><SplitTests /></ProtectedRoute>} />
            <Route path="/admin/split-tests/new" element={<ProtectedRoute><SplitTestCreate /></ProtectedRoute>} />
            <Route path="/admin/split-tests/:id" element={<ProtectedRoute><SplitTestResults /></ProtectedRoute>} />
            <Route path="/admin/split-tests/:id/edit" element={<ProtectedRoute><SplitTestCreate /></ProtectedRoute>} />
            
            {/* Quiz Routes */}
            <Route path="/" element={<Quiz />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/:slug" element={<QuizWithSlug />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
